"use client";

import { useId, useRef } from "react";
import { FileText, ImageIcon, Paperclip, X } from "lucide-react";
import { useDict } from "@/components/i18n/DictProvider";
import { humanSize } from "@/lib/format";
import { FILE_ACCEPT, fileProblem, MAX_FILES } from "@/lib/issues/rules";

/**
 * The files for a report or a reply, held by the parent form (a file input
 * cannot be controlled, and React resets uncontrolled fields after an action —
 * a refused send must not cost the member their screenshots). Each pick is
 * checked with the same rules the Server Action and the backend apply, and a
 * pick that would break them is refused whole, with the reason, instead of
 * half-added.
 */
export function FilePicker({
  files,
  onChange,
  error,
  onError,
  already = 0,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  error: string | null;
  onError: (message: string | null) => void;
  /** Files the report already holds — the cap of five counts them. */
  already?: number;
}) {
  const t = useDict().issues;
  const input = useRef<HTMLInputElement>(null);
  const hintId = useId();
  const left = MAX_FILES - already - files.length;

  function add(list: FileList | null) {
    const picked = Array.from(list ?? []);
    if (input.current) input.current.value = "";
    if (!picked.length) return;
    const next = [...files, ...picked];
    const problem = fileProblem(next, already);
    if (problem) {
      onError(problem === "count" ? t.fileCount : problem === "size" ? t.fileSize : t.fileType);
      return;
    }
    onError(null);
    onChange(next);
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="font-display font-semibold text-[14px] leading-5 text-foreground">
        {t.filesLabel}
      </span>

      {files.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {files.map((f, i) => {
            const Icon = f.type.startsWith("image/") ? ImageIcon : FileText;
            return (
              <li
                key={`${f.name}-${f.size}-${i}`}
                className="flex items-center gap-3 rounded-lg border border-border/70 bg-background px-3 py-2 text-[13px]"
              >
                <Icon className="size-4 shrink-0 text-foreground/50" aria-hidden />
                <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                  {f.name}
                </span>
                <span className="shrink-0 text-muted-foreground">{humanSize(f.size)}</span>
                <button
                  type="button"
                  onClick={() => {
                    onError(null);
                    onChange(files.filter((_, j) => j !== i));
                  }}
                  aria-label={t.removeFile.replaceAll("{name}", f.name)}
                  className="-mr-1 grid size-7 shrink-0 place-items-center rounded-full text-foreground/45 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="size-3.5" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={left <= 0}
          aria-describedby={hintId}
          className="inline-flex h-10 items-center gap-2 rounded-pill border border-dashed border-border px-4 text-[13px] font-medium text-foreground transition-colors hover:border-foreground/30 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Paperclip className="size-4 text-foreground/55" aria-hidden />
          {t.addFiles}
        </button>
        <input
          ref={input}
          type="file"
          multiple
          accept={FILE_ACCEPT}
          className="sr-only"
          tabIndex={-1}
          aria-hidden
          onChange={(e) => add(e.currentTarget.files)}
        />
        <span id={hintId} className="text-[12px] leading-4 text-mid-gray">
          {t.filesHint}
          {already > 0 ? ` ${t.filesLeft.replaceAll("{n}", String(Math.max(left, 0)))}` : null}
        </span>
      </div>

      {error ? (
        <span role="alert" className="text-[12px] leading-4 text-destructive">
          {error}
        </span>
      ) : null}
    </div>
  );
}
